import type { CustomIconEntry } from '../types';

import AiLoadingSparkleIcon from './svgs/ai-loading-sparkle.svg?react';
import AiCustomAutomationIcon from './svgs/ai_custom_automation.svg?react';
import AiCustomAutomationFilledIcon from './svgs/ai_custom_automation_filled.svg?react';
import AiSparkleIcon from './svgs/ai_sparkle.svg?react';
import BooleanIcon from './svgs/boolean.svg?react';
import ClauseIcon from './svgs/clause-section.svg?react';
import EvisortAiStaticIcon from './svgs/evisort-ai-static.svg?react';
import FileDocIcon from './svgs/file-doc.svg?react';
import FileImageIcon from './svgs/file-image.svg?react';
import FilePdfIcon from './svgs/file-pdf.svg?react';
import FileSheetIcon from './svgs/file-sheet.svg?react';
import FileSlideIcon from './svgs/file-slide.svg?react';
import SortAscendingIcon from './svgs/sort-ascending.svg?react';
import SortDescendingIcon from './svgs/sort-descending.svg?react';
import WorkdayIcon from './svgs/wd-icon-workday.svg?react';

/** Evisort brand, file-type, and animated loading icons (`./svgs/*.svg` via SVGR `?react`). */
export const CUSTOM_ICON_ENTRIES = {
  'ai-custom-automation': {
    type: 'custom',
    component: AiCustomAutomationIcon,
    filled: AiCustomAutomationFilledIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
    description:
      'Use as a decorative indicator for the hub or features related to setting up custom AI workflows/models for day-to-day use (e.g., nav entry into the automation hub, cards representing individual custom workflows).',
  },
  'ai-loading-sparkle': {
    type: 'custom',
    component: AiLoadingSparkleIcon,
    viewBox: '0 0 40 12',
    preserveColors: true,
  },
  'ai-sparkle': {
    type: 'custom',
    component: AiSparkleIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
    description:
      'Use as a general decorative indicator for AI-powered features or AI-generated content (e.g., "AI suggestion," "generated by AI" tag, entry point to an AI assist feature). No specific functional meaning beyond signaling "AI is involved here". Use sparingly. The platform is an AI product already. if every AI-touched surface gets a sparkle, the icon stops signaling anything and becomes visual noise. Reserve ai_sparkle for moments where calling out "this specific thing is AI" adds real clarity (e.g., distinguishing an AI-generated suggestion from a user-entered value in the same view), not as a default badge on every feature that happens to use AI under the hood.',
  },
  boolean: {
    type: 'custom',
    component: BooleanIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
    description:
      'Use to indicate a static field (read-only display of a value) displaying a true/false (T/F) value, e.g., "Is active," "Verified," yes/no flags.',
  },
  clause: {
    type: 'custom',
    component: ClauseIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
    description:
      'Use to indicate a feature or page that points to clauses extracted from a legal document (§ is the universal shorthand for a legal clause/section). Marks the destination/section for extracted clause content, do not attach an icon for each individual clause instance.',
  },
  'evisort-ai-static': {
    type: 'custom',
    component: EvisortAiStaticIcon,
    viewBox: '0 0 28 28',
    preserveColors: true,
  },
  'file-doc': {
    type: 'custom',
    component: FileDocIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
  },
  'file-image': {
    type: 'custom',
    component: FileImageIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
  },
  'file-pdf': {
    type: 'custom',
    component: FilePdfIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
  },
  'file-sheet': {
    type: 'custom',
    component: FileSheetIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
  },
  'file-slide': {
    type: 'custom',
    component: FileSlideIcon,
    viewBox: '0 0 24 24',
    preserveColors: true,
  },
  'sort-ascending': {
    type: 'custom',
    component: SortAscendingIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
    description:
      'Use to indicate or apply ascending order (e.g., A to Z, low to high, oldest to newest).',
  },
  'sort-descending': {
    type: 'custom',
    component: SortDescendingIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
    description:
      'Use to indicate or apply descending order (e.g., Z to A, high to low, newest to oldest). The direct counterpart to sort_ascending.',
  },
  workday: {
    type: 'custom',
    component: WorkdayIcon,
    viewBox: '0 0 40 40',
    preserveColors: true,
  },
} as const satisfies Record<string, CustomIconEntry>;
